function _classPrivateFieldLooseBase(e, t) { if (!{}.hasOwnProperty.call(e, t)) throw new TypeError("attempted to use private field on non-instance"); return e; }
var id = 0;
function _classPrivateFieldLooseKey(e) { return "__private_" + id++ + "_" + e; }
import { Component } from '@theme/component';
import { debounce } from '@theme/utilities';
const ANIMATION_OPTIONS = {
  duration: 500
};

/**
 * A custom element that displays a marquee.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} wrapper - The wrapper element.
 * @property {HTMLElement} content - The content element.
 * @property {HTMLElement[]} marqueeItems - The marquee items collection.
 *
 * @extends Component<Refs>
 */
var _animation = _classPrivateFieldLooseKey("animation");
var _marqueeWidth = _classPrivateFieldLooseKey("marqueeWidth");
var _slowDown = _classPrivateFieldLooseKey("slowDown");
var _speedUp = _classPrivateFieldLooseKey("speedUp");
var _setSpeed = _classPrivateFieldLooseKey("setSpeed");
var _queryNumberOfCopies = _classPrivateFieldLooseKey("queryNumberOfCopies");
var _calculateSpeed = _classPrivateFieldLooseKey("calculateSpeed");
var _handleResize = _classPrivateFieldLooseKey("handleResize");
var _restartAnimation = _classPrivateFieldLooseKey("restartAnimation");
var _duplicateContent = _classPrivateFieldLooseKey("duplicateContent");
var _addRepeatedItems = _classPrivateFieldLooseKey("addRepeatedItems");
var _removeRepeatedItems = _classPrivateFieldLooseKey("removeRepeatedItems");
class MarqueeComponent extends Component {
  constructor(...args) {
    super(...args);
    /**
     * @param {number} numberOfCopies
     */
    Object.defineProperty(this, _removeRepeatedItems, {
      value: _removeRepeatedItems2
    });
    /**
     * @param {number} numberOfCopies
     */
    Object.defineProperty(this, _addRepeatedItems, {
      value: _addRepeatedItems2
    });
    Object.defineProperty(this, _duplicateContent, {
      value: _duplicateContent2
    });
    Object.defineProperty(this, _restartAnimation, {
      value: _restartAnimation2
    });
    /**
     * @param {number} numberOfCopies
     */
    Object.defineProperty(this, _calculateSpeed, {
      value: _calculateSpeed2
    });
    Object.defineProperty(this, _queryNumberOfCopies, {
      value: _queryNumberOfCopies2
    });
    /**
     * @param {number} value
     */
    Object.defineProperty(this, _setSpeed, {
      value: _setSpeed2
    });
    Object.defineProperty(this, _speedUp, {
      value: _speedUp2
    });
    this.requiredRefs = ['wrapper', 'content', 'marqueeItems'];
    /**
     * @type {{ cancel: () => void, current: number } | null}
     */
    Object.defineProperty(this, _animation, {
      writable: true,
      value: null
    });
    /**
     * @type {number | null}
     */
    Object.defineProperty(this, _marqueeWidth, {
      writable: true,
      value: null
    });
    Object.defineProperty(this, _slowDown, {
      writable: true,
      value: debounce(() => {
        if (_classPrivateFieldLooseBase(this, _animation)[_animation]) return;
        const animation = this.refs.wrapper.getAnimations()[0];
        if (!animation) return;
        _classPrivateFieldLooseBase(this, _animation)[_animation] = animateValue({
          ...ANIMATION_OPTIONS,
          from: 1,
          to: 0,
          onUpdate: value => animation.updatePlaybackRate(value),
          onComplete: () => {
            _classPrivateFieldLooseBase(this, _animation)[_animation] = null;
          }
        });
      }, ANIMATION_OPTIONS.duration)
    });
    Object.defineProperty(this, _handleResize, {
      writable: true,
      value: debounce(async () => {
        const {
          marqueeItems
        } = this.refs;
        const {
          newNumberOfCopies,
          isHorizontalResize
        } = await _classPrivateFieldLooseBase(this, _queryNumberOfCopies)[_queryNumberOfCopies]();

        // opt out of marquee manipulation on vertical resizes
        if (!isHorizontalResize) return;
        const currentNumberOfCopies = marqueeItems.length;
        const speed = _classPrivateFieldLooseBase(this, _calculateSpeed)[_calculateSpeed](newNumberOfCopies);
        if (newNumberOfCopies > currentNumberOfCopies) {
          _classPrivateFieldLooseBase(this, _addRepeatedItems)[_addRepeatedItems](newNumberOfCopies - currentNumberOfCopies);
        } else if (newNumberOfCopies < currentNumberOfCopies) {
          _classPrivateFieldLooseBase(this, _removeRepeatedItems)[_removeRepeatedItems](currentNumberOfCopies - newNumberOfCopies);
        }
        _classPrivateFieldLooseBase(this, _duplicateContent)[_duplicateContent]();
        _classPrivateFieldLooseBase(this, _setSpeed)[_setSpeed](speed);
        _classPrivateFieldLooseBase(this, _restartAnimation)[_restartAnimation]();
      }, 250)
    });
  }
  async connectedCallback() {
    super.connectedCallback();
    const {
      marqueeItems
    } = this.refs;
    if (marqueeItems.length === 0) return;
    const {
      numberOfCopies
    } = await _classPrivateFieldLooseBase(this, _queryNumberOfCopies)[_queryNumberOfCopies]();
    const speed = _classPrivateFieldLooseBase(this, _calculateSpeed)[_calculateSpeed](numberOfCopies);
    _classPrivateFieldLooseBase(this, _addRepeatedItems)[_addRepeatedItems](numberOfCopies);
    _classPrivateFieldLooseBase(this, _duplicateContent)[_duplicateContent]();
    _classPrivateFieldLooseBase(this, _setSpeed)[_setSpeed](speed);
    window.addEventListener('resize', _classPrivateFieldLooseBase(this, _handleResize)[_handleResize]);
    this.addEventListener('pointerenter', _classPrivateFieldLooseBase(this, _slowDown)[_slowDown]);
    this.addEventListener('pointerleave', _classPrivateFieldLooseBase(this, _speedUp)[_speedUp]);
  }
  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('resize', _classPrivateFieldLooseBase(this, _handleResize)[_handleResize]);
    this.removeEventListener('pointerenter', _classPrivateFieldLooseBase(this, _slowDown)[_slowDown]);
    this.removeEventListener('pointerleave', _classPrivateFieldLooseBase(this, _speedUp)[_speedUp]);
  }
  get clonedContent() {
    const {
      content,
      wrapper
    } = this.refs;
    const lastChild = wrapper.lastElementChild;
    return content !== lastChild ? lastChild : null;
  }
}

// Define the animateValue function
/**
 * Animate a numeric property smoothly.
 * @param {Object} params - The parameters for the animation.
 * @param {number} params.from - The starting value.
 * @param {number} params.to - The ending value.
 * @param {number} params.duration - The duration of the animation in milliseconds.
 * @param {function(number): void} params.onUpdate - The function to call on each update.
 * @param {function(number): number} [params.easing] - The easing function.
 * @param {function(): void} [params.onComplete] - The function to call when the animation completes.
 */
function _speedUp2() {
  _classPrivateFieldLooseBase(this, _slowDown)[_slowDown].cancel();
  const animation = this.refs.wrapper.getAnimations()[0];
  if (!animation || animation.playbackRate === 1) return;
  const from = _classPrivateFieldLooseBase(this, _animation)[_animation]?.current ?? 0;
  _classPrivateFieldLooseBase(this, _animation)[_animation]?.cancel();
  _classPrivateFieldLooseBase(this, _animation)[_animation] = animateValue({
    ...ANIMATION_OPTIONS,
    from,
    to: 1,
    onUpdate: value => animation.updatePlaybackRate(value),
    onComplete: () => {
      _classPrivateFieldLooseBase(this, _animation)[_animation] = null;
    }
  });
}
function _setSpeed2(value) {
  this.style.setProperty('--marquee-speed', `${value}s`);
}
async function _queryNumberOfCopies2() {
  const {
    marqueeItems
  } = this.refs;
  return new Promise(resolve => {
    if (!marqueeItems[0]) {
      // Wrapping the resolve in a setTimeout here and below splits each marquee reflow into a separate task.
      return setTimeout(() => resolve({
        numberOfCopies: 1,
        isHorizontalResize: true
      }), 0);
    }
    const intersectionObserver = new IntersectionObserver(entries => {
      const firstEntry = entries[0];
      if (!firstEntry) return;
      intersectionObserver.disconnect();
      const {
        width: marqueeWidth
      } = firstEntry.rootBounds ?? {
        width: 0
      };
      const {
        width: marqueeItemsWidth
      } = firstEntry.boundingClientRect;
      const isHorizontalResize = _classPrivateFieldLooseBase(this, _marqueeWidth)[_marqueeWidth] !== marqueeWidth;
      _classPrivateFieldLooseBase(this, _marqueeWidth)[_marqueeWidth] = marqueeWidth;
      setTimeout(() => {
        resolve({
          numberOfCopies: marqueeItemsWidth === 0 ? 1 : Math.ceil(marqueeWidth / marqueeItemsWidth),
          isHorizontalResize
        });
      }, 0);
    }, {
      root: this
    });
    intersectionObserver.observe(marqueeItems[0]);
  });
}
function _calculateSpeed2(numberOfCopies) {
  const speedFactor = Number(this.getAttribute('data-speed-factor'));
  const speed = Math.sqrt(numberOfCopies) * speedFactor;
  return speed;
}
function _restartAnimation2() {
  const animations = this.refs.wrapper.getAnimations();
  requestAnimationFrame(() => {
    for (const animation of animations) {
      animation.currentTime = 0;
    }
  });
}
function _duplicateContent2() {
  this.clonedContent?.remove();
  const clone = /** @type {HTMLElement} */this.refs.content.cloneNode(true);
  clone.setAttribute('aria-hidden', 'true');
  clone.removeAttribute('ref');
  this.refs.wrapper.appendChild(clone);
}
function _addRepeatedItems2(numberOfCopies) {
  const {
    content,
    marqueeItems
  } = this.refs;
  if (!marqueeItems[0]) return;
  for (let i = 0; i < numberOfCopies - 1; i++) {
    const clone = marqueeItems[0].cloneNode(true);
    content.appendChild(clone);
  }
}
function _removeRepeatedItems2(numberOfCopies) {
  const {
    content
  } = this.refs;
  const children = Array.from(content.children);
  const itemsToRemove = Math.min(numberOfCopies, children.length - 1);
  for (let i = 0; i < itemsToRemove; i++) {
    content.lastElementChild?.remove();
  }
}
function animateValue({
  from,
  to,
  duration,
  onUpdate,
  easing = t => t * t * (3 - 2 * t),
  onComplete
}) {
  const startTime = performance.now();
  let cancelled = false;
  let currentValue = from;

  /**
   * @param {number} currentTime - The current time in milliseconds.
   */
  function animate(currentTime) {
    if (cancelled) return;
    const elapsed = currentTime - startTime;
    const progress = Math.min(elapsed / duration, 1);
    const easedProgress = easing(progress);
    currentValue = from + (to - from) * easedProgress;
    onUpdate(currentValue);
    if (progress < 1) {
      requestAnimationFrame(animate);
    } else if (typeof onComplete === 'function') {
      onComplete();
    }
  }
  requestAnimationFrame(animate);
  return {
    get current() {
      return currentValue;
    },
    cancel() {
      cancelled = true;
    }
  };
}
if (!customElements.get('marquee-component')) {
  customElements.define('marquee-component', MarqueeComponent);
}
